/**
 * @file Label for FormItem
 * @author chenqiang(chenqiang03)
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import OutlinedQuestionCircle from 'acud-icon/lib/icons/OutlinedQuestionCircle';
import Col from '../grid/col';
import { FormContext } from './context';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import defaultLocale from '../locale/default';
import Tooltip from '../tooltip';
import { getCSSNumbericValue } from './util';
import { ConfigContext } from '../config-provider';
function toTooltipProps(tooltip) {
    if (!tooltip) {
        return null;
    }
    if (typeof tooltip === 'object' && !React.isValidElement(tooltip)) {
        return tooltip;
    }
    return {
        title: tooltip,
    };
}
const FormItemLabel = ({ name, prefixCls, label, htmlFor, labelCol, labelAlign, labelWidth, colon, required, requiredMark, tooltip, labelExtra, }) => {
    const [formLocale] = useLocaleReceiver('Form');
    const { locale } = React.useContext(ConfigContext);
    const ellipsedRef = React.useRef();
    // isEllipsed只作用于英文环境
    const [isEllipsed, setIsEllipsed] = React.useState(false);
    // 渲染label旁边的自定义组件
    const renderLabelExtra = React.useCallback(() => {
        if (React.isValidElement(labelExtra)) {
            return React.cloneElement(labelExtra, { name });
        }
        else if (labelExtra) {
            const LabelExtra = labelExtra;
            return (<LabelExtra name={name}/>);
        }
        return null;
    }, [labelExtra, label]);
    React.useLayoutEffect(() => {
        if (!ellipsedRef.current || (locale === null || locale === void 0 ? void 0 : locale.locale) !== 'en') {
            return;
        }
        setIsEllipsed(ellipsedRef.current.scrollHeight > ellipsedRef.current.clientHeight);
    }, [label]);
    // 允许水平方向label设置空字符串作为占位符
    if (typeof label === 'undefined') {
        return null;
    }
    return (<FormContext.Consumer key="label">
            {({ vertical, labelAlign: contextLabelAlign, labelCol: contextLabelCol, labelWidth: contextLabelWidth, colon: contextColon, }) => {
            var _a;
            // 取消垂直布局label为空字符串的占位功能
            if (!label && vertical) {
                return null;
            }
            const mergedLabelCol = labelCol || contextLabelCol || {};
            const mergedLabelAlign = labelAlign || contextLabelAlign;
            const mergedLabelWidth = getCSSNumbericValue(labelWidth || contextLabelWidth);
            const labelClsBasic = `${prefixCls}-item-label`;
            const labelColClassName = classNames(labelClsBasic, mergedLabelAlign === 'left' && `${labelClsBasic}-left`, mergedLabelCol.className);
            let labelChildren = [label];
            // Keep label is original where there should have no colon
            const computedColon = colon === true || (contextColon !== false && colon !== false);
            const haveColon = computedColon
                && !vertical
                && (typeof label === 'string' && label.trim() !== '');
            // Remove duplicated user input colon
            if (haveColon) {
                labelChildren = [label.replace(/[:|：]\s*$/, '')];
            }
            const tooltipProps = toTooltipProps(tooltip);
            if (tooltipProps) {
                const { icon = <OutlinedQuestionCircle /> } = tooltipProps, restTooltipProps = __rest(tooltipProps, ["icon"]);
                const tooltipNode = (<Tooltip {...restTooltipProps}>
                            {React.cloneElement(icon, { className: `${prefixCls}-item-tooltip` })}
                        </Tooltip>);
                labelChildren.push(tooltipNode);
            }
            // Add required mark if optional
            if (requiredMark === 'optional' && !required) {
                labelChildren.push(<span className={`${prefixCls}-item-optional`}>
                            {(formLocale === null || formLocale === void 0 ? void 0 : formLocale.optional) || ((_a = defaultLocale.Form) === null || _a === void 0 ? void 0 : _a.optional)}
                        </span>);
            }
            const labelClassName = classNames({
                [`${prefixCls}-item-required`]: required,
                [`${prefixCls}-item-required-mark-optional`]: requiredMark === 'optional',
                [`${prefixCls}-item-no-colon`]: !haveColon,
                [`${prefixCls}-item-has-tooltip`]: !!tooltipProps,
                [`${prefixCls}-item-label-ellipsed`]: isEllipsed,
            });
            const labelStyle = !vertical ? {
                width: mergedLabelWidth
            } : {};
            return (<Col {...mergedLabelCol} style={labelStyle} className={labelColClassName}>
                        <label htmlFor={htmlFor} className={labelClassName} title={typeof label === 'string' ? label : ''}>
                            {(locale === null || locale === void 0 ? void 0 : locale.locale) === 'en' ? <>
                                <div className={`${prefixCls}-item-label-content`} ref={ellipsedRef}>
                                    {labelChildren[0]}
                                    <span className={classNames({
                        [`${prefixCls}-item-label-content-opt-ellipsed`]: isEllipsed,
                        [`${prefixCls}-item-label-content-opt-not-ellipsed`]: !isEllipsed
                    })}>
                                        {labelChildren.slice(1)}
                                    </span>
                                </div>
                            </>
                    : labelChildren}
                        </label>
                        {(labelExtra && vertical) && (<div className={`${prefixCls}-item-label-extra`}>
                                {renderLabelExtra()}
                            </div>)}
                    </Col>);
        }}
        </FormContext.Consumer>);
};
export default FormItemLabel;
